import type { QuotesResponse } from '@/pages/api/cross-chain/quote';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import type { CrossChainQuote } from '@/lib/api/0x-cross-chain/types';
import { ClientDataError, ClientNetworkError } from '@/lib/errors/client';

import { ZERO_X_CROSS_CHAIN_ERROR } from '@/constants/errors';

import { getFullURL } from '@/utils/fetcher';
import { ChainAddress } from '@/utils/models';
import { routes } from '@/utils/routes';
import { withSentSlippage } from '@/utils/slippage/sentSlippage';

/**
 * Parameters for the 0x cross-chain quotes hook.
 */
export interface CrossChainQuoteParams {
  /** Origin chain ID (number or 'solana'). */
  originChain: number | string;
  /** Destination chain ID. */
  destinationChain: number | string;
  /** Sell token address. */
  sellToken: string;
  /** Buy token address. */
  buyToken: string;
  /** Sell amount in base units. */
  sellAmount: string;
  /** Origin wallet address. */
  originAddress: ChainAddress;
  /** Destination wallet address (optional, for cross-chain to different wallet). */
  destinationAddress?: ChainAddress;
  /** Slippage in basis points (optional). */
  slippageBps?: number;
  /** Mode used to resolve slippage. Included in the query key, not the request URL. */
  slippageMode: 'auto' | 'manual';
  /** Sort criterion for the 0x API: 'price' for best output, 'speed' for fastest. */
  sortQuotesBy: 'price' | 'speed';
}

/**
 * Fetcher for 0x cross-chain quotes.
 */
const fetcher = async (params: CrossChainQuoteParams): Promise<CrossChainQuote | undefined> => {
  const queryParams = new URLSearchParams({
    originChain: params.originChain.toString(),
    destinationChain: params.destinationChain.toString(),
    sellToken: params.sellToken,
    buyToken: params.buyToken,
    sellAmount: params.sellAmount,
    originAddress: params.originAddress,
    sortQuotesBy: params.sortQuotesBy,
    ...(params.destinationAddress && { destinationAddress: params.destinationAddress }),
    ...(params.slippageBps != null && { slippageBps: params.slippageBps.toString() }),
  });

  const url = getFullURL(`${routes.api.CROSS_CHAIN_QUOTE}?${queryParams.toString()}`);
  const response = await fetch(url);

  if (response.status === 204) {
    throw new ClientDataError(ZERO_X_CROSS_CHAIN_ERROR.NO_QUOTES_AVAILABLE);
  }

  if (!response.ok) {
    const reason = await response
      .json()
      .then((body) => body?.reason)
      .catch(() => undefined);
    throw new ClientNetworkError(reason || ZERO_X_CROSS_CHAIN_ERROR.FAILED_TO_FETCH_QUOTE);
  }

  try {
    const data: QuotesResponse = await response.json();
    if (!data.success) throw new ClientDataError(ZERO_X_CROSS_CHAIN_ERROR.FAILED_TO_FETCH_QUOTE);
    return withSentSlippage(data.result.quote, url, params.slippageMode === 'auto');
  } catch (err) {
    if (err instanceof ClientDataError || err instanceof ClientNetworkError) throw err;
    throw new ClientDataError(ZERO_X_CROSS_CHAIN_ERROR.FAILED_TO_FETCH_QUOTE);
  }
};

export type CrossChainQuotesQueryOptions = Omit<
  UseQueryOptions<CrossChainQuote | undefined, Error>,
  'queryKey' | 'queryFn' | 'enabled'
>;

export const CROSS_CHAIN_QUOTE_REFRESH_MS = 10_000;

export const crossChainQuotesQueryKey = (params: CrossChainQuoteParams | undefined) =>
  ['cross-chain-quote', params] as const;

/**
 * Hook to fetch 0x cross-chain quotes.
 *
 * @param params - Quote request parameters. Pass `undefined` to disable the fetch.
 * @param options - React Query configuration options.
 */
export const useCrossChainQuotes = (
  params: CrossChainQuoteParams | undefined,
  options?: CrossChainQuotesQueryOptions,
): UseQueryResult<CrossChainQuote | undefined, Error> =>
  useQuery<CrossChainQuote | undefined, Error>({
    queryKey: crossChainQuotesQueryKey(params),
    queryFn: () => fetcher(params!),
    enabled: !!params,
    refetchInterval: CROSS_CHAIN_QUOTE_REFRESH_MS,
    ...options,
  });
